// Bonus income module (CAXeng.md §5.3): prior-relationship bonus allocation tables, the "Taxes Apportioned"
// 12-line explanation for a uniform / variable bonus (GetUniformBonusTaxx / GetVariableBonusTaxx) and the
// support-vs-bonus tables (GetAdditionalWages / GetAnnualBonus / GetBonuses).
//
// Units: Case money is MONTHLY. A bonus is a per-payment amount (monthly for the monthly table and the uniform
// explanation, annual for the annual table). Tax lines of BonusTaxResult are ANNUAL; the parts (lines 9–11) and
// the net increase (line 12) are for the bonus payment under consideration.
import type { BonusAllocationRow, BonusTable, BonusTableRow, BonusTaxResult, Case, PartyIndex, PartyInputs } from '@divorceos/schema';
import { computeGuideline, payorOf, receivedBy, type GuidelineOutcome } from './guideline.js';
import { normalizeCase } from './normalize.js';
import { providerOf, type EngineOptions, type TaxProvider } from './provider.js';
import { ANNUAL, NO_SUPPORT, TaxContext, type TaxOutcome } from './taxes.js';

// ------------------------------------------------------------------------------------- allocation table

/** Percentages (0..100) of a bonus that go to prior relationships as CS / traditional SS / partner (DPRRA) SS. */
export interface BonusAllocation { csPercent: number; ssPercent: number; otherPercent: number }

const ZERO_ALLOCATION: BonusAllocation = { csPercent: 0, ssPercent: 0, otherPercent: 0 };

/** Rows of the 15-row allocation table with an amount below this are unused (`_DAT_1001e3c8` = 99). */
export const BONUS_TABLE_MIN_AMOUNT = 99;
/** Bracketing tolerance of the original's row search. */
const BRACKET_EPS = 0.01;

const pctOf = (r: BonusAllocationRow): BonusAllocation => ({ csPercent: r.csPercent, ssPercent: r.ssPercent, otherPercent: r.otherPercent });

/**
 * §5.3 FUN_10001267: linear interpolation of the allocation percentages between the two table rows bracketing
 * `bonus`. Rows with amount < 99 are ignored; outside the table the nearest row is used; an empty table gives zeros.
 */
export function interpolateAllocation(table: readonly BonusAllocationRow[] | undefined, bonus: number): BonusAllocation {
  const rows = (table ?? [])
    .filter((r) => Number.isFinite(r.amount) && r.amount >= BONUS_TABLE_MIN_AMOUNT)
    .sort((a, b) => a.amount - b.amount);
  if (rows.length === 0 || !Number.isFinite(bonus)) return { ...ZERO_ALLOCATION };
  const first = rows[0], last = rows[rows.length - 1];
  if (bonus <= first.amount + BRACKET_EPS) return pctOf(first);
  if (bonus >= last.amount - BRACKET_EPS) return pctOf(last);
  for (let i = 0; i + 1 < rows.length; i++) {
    const lo = rows[i], hi = rows[i + 1];
    if (bonus < lo.amount - BRACKET_EPS || bonus > hi.amount + BRACKET_EPS) continue;
    const span = hi.amount - lo.amount;
    if (span <= BRACKET_EPS) return pctOf(Math.abs(bonus - hi.amount) <= BRACKET_EPS ? hi : lo);
    const t = Math.min(1, Math.max(0, (bonus - lo.amount) / span));
    return {
      csPercent: lo.csPercent + t * (hi.csPercent - lo.csPercent),
      ssPercent: lo.ssPercent + t * (hi.ssPercent - lo.ssPercent),
      otherPercent: lo.otherPercent + t * (hi.otherPercent - lo.otherPercent),
    };
  }
  return pctOf(last);
}

// ------------------------------------------------------------------------------------- case plumbing

interface BonusData { periods: number; monthly: number[]; table: BonusAllocationRow[] }

/** Bonus inputs of `party`; a missing `c.bonus` means 12 periods, no anticipated monthly bonuses, empty table. */
function bonusData(c: Case, party: PartyIndex): BonusData {
  const b = c.bonus;
  const periods = b?.periodsPerYear?.[party];
  const monthly = Array.from({ length: ANNUAL }, (_, m) => {
    const v = b?.variableMonthly?.[party]?.[m];
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  });
  return {
    periods: typeof periods === 'number' && Number.isFinite(periods) ? periods : ANNUAL,
    monthly,
    table: b?.table?.[party] ?? [],
  };
}

/** Dollar parts (same units as `bonus`) of one bonus payment that go to prior relationships. */
interface BonusParts { cs: number; ss: number; other: number }
const ZERO_PARTS: BonusParts = { cs: 0, ss: 0, other: 0 };

function partsOf(table: readonly BonusAllocationRow[], bonus: number, use: boolean): BonusParts {
  if (!use || bonus === 0) return { ...ZERO_PARTS };
  const a = interpolateAllocation(table, bonus);
  return { cs: bonus * a.csPercent / 100, ss: bonus * a.ssPercent / 100, other: bonus * a.otherPercent / 100 };
}

const addParts = (a: BonusParts, b: BonusParts, k = 1): BonusParts => ({ cs: a.cs + k * b.cs, ss: a.ss + k * b.ss, other: a.other + k * b.other });

/**
 * The case with an ANNUAL bonus total added to `party`'s monthly wages (÷12) and the annual prior-relationship
 * parts added to its CS paid / SS paid (fed-deductible) / partner SS paid for prior relationships (÷12).
 * §5.3: wages(p) += total/12; 0x518 += CS part/12; 0x4b4 += SS part/12; 0x4c4 += other part/12.
 */
export function caseWithBonus(c: Case, party: PartyIndex, annualBonus: number, annualParts: BonusParts = ZERO_PARTS): Case {
  const p = c.parties[party];
  const np: PartyInputs = {
    ...p,
    wages: p.wages + annualBonus / ANNUAL,
    csPaidPrior: p.csPaidPrior + annualParts.cs / ANNUAL,
    ssPaidPrior: {
      ...p.ssPaidPrior,
      thisSpouseFedDeductible: p.ssPaidPrior.thisSpouseFedDeductible + annualParts.ss / ANNUAL,
      partnerByThisSpouse: p.ssPaidPrior.partnerByThisSpouse + annualParts.other / ANNUAL,
    },
  };
  const parties: [PartyInputs, PartyInputs] = party === 0 ? [np, c.parties[1]] : [c.parties[0], np];
  return { ...c, parties };
}

// ------------------------------------------------------------------------------------- tax lines

/** Annual taxes of `party`'s return in the no-support scenario: the whole return (line 1/3) and the party's prorated share (line 2/4). */
interface ReturnTaxes { whole: number; share: number; outcome: TaxOutcome }

/**
 * Federal + state income taxes and employment taxes (FICA, SE, SDI) of the return as filed, annual. For a joint-in
 * return both parties' attributed taxes are summed (employment taxes per earner, as the engine attributes them
 * everywhere else), so line 1 = this party's share + the other party's share; otherwise the return's own figures
 * (for MFJOUT including the new spouse's), which coincide with the share for a non-joint filer.
 */
function wholeReturnTax(t: [TaxOutcome, TaxOutcome], party: PartyIndex, jointIn: boolean): number {
  if (jointIn) return (t[0].incomeTax + t[0].employmentTax + t[1].incomeTax + t[1].employmentTax) * ANNUAL;
  const r = t[party];
  return r.federal.netIncomeTax + r.state.netIncomeTax + r.federal.ficaSocialSecurity + r.federal.ficaMedicare + r.federal.selfEmploymentTax + r.state.sdi;
}

function noSupportReturnTaxes(c: Case, party: PartyIndex, provider: TaxProvider): ReturnTaxes {
  const nc = normalizeCase(c);
  const ctx = new TaxContext(nc, provider);
  const t = ctx.scenario(NO_SUPPORT);
  // Share = the engine's prorated per-party taxes (income taxes × joint ratio + the party's own employment taxes).
  return { whole: wholeReturnTax(t, party, nc.prorateJoint), share: t[party].taxes.total * ANNUAL, outcome: t[party] };
}

const isJointIn = (c: Case): boolean => c.parties[0].filingStatus === 'MFJIN' && c.parties[1].filingStatus === 'MFJIN';

function zeroBonusTax(party: PartyIndex, bonus: number, periods: number): BonusTaxResult {
  return {
    party, bonus, periodsPerYear: periods,
    annualTaxBase: 0, annualTaxBaseShare: 0, annualTaxBonus: 0, annualTaxBonusShare: 0, additionalTax: 0,
    totalBonus: 0, fraction: 0, apportionedTax: 0, additionalCs: 0, additionalSsTraditional: 0, additionalSsPartner: 0, netIncrease: 0,
  };
}

/** The 12 lines given the year's total bonus, the annual prior parts, the bonus month's fraction and parts. */
function bonusLines(
  c: Case, party: PartyIndex, bonus: number, periods: number, totalBonus: number, annualParts: BonusParts,
  fraction: number, monthParts: BonusParts, provider: TaxProvider,
): BonusTaxResult {
  const base = noSupportReturnTaxes(c, party, provider);
  const withBonus = noSupportReturnTaxes(caseWithBonus(c, party, totalBonus, annualParts), party, provider);
  const additionalTax = withBonus.share - base.share;
  const apportionedTax = additionalTax * fraction;
  return {
    party, bonus, periodsPerYear: periods,
    annualTaxBase: base.whole,
    annualTaxBaseShare: base.share,
    annualTaxBonus: withBonus.whole,
    annualTaxBonusShare: withBonus.share,
    additionalTax,
    totalBonus,
    fraction,
    apportionedTax,
    additionalCs: monthParts.cs,
    additionalSsTraditional: monthParts.ss,
    additionalSsPartner: monthParts.other,
    netIncrease: bonus - apportionedTax - monthParts.cs - monthParts.ss - monthParts.other,
  };
}

/**
 * GetUniformBonusTaxx: tax apportionment for a bonus of `bonus` paid `periodsPerYear[party]` times a year.
 * Fraction in the bonus period = 1/periods (1/12 when both parties file MFJ-in, as the original). All zero when periods ≤ 0.
 */
export function uniformBonusTax(c: Case, party: PartyIndex, bonus: number, usePriorTable: boolean, opts: EngineOptions = {}): BonusTaxResult {
  const { periods, table } = bonusData(c, party);
  if (!(periods > 0) || !Number.isFinite(bonus)) return zeroBonusTax(party, bonus, periods);
  const provider = providerOf(opts);
  const monthParts = partsOf(table, bonus, usePriorTable);
  const annualParts = addParts({ ...ZERO_PARTS }, monthParts, periods);
  const fraction = isJointIn(c) ? 1 / ANNUAL : 1 / periods;
  return bonusLines(c, party, bonus, periods, periods * bonus, annualParts, fraction, monthParts, provider);
}

/** Floor of the year's total variable bonuses used as the fraction's denominator (§5.3 "floor 0.1"). */
const VARIABLE_TOTAL_FLOOR = 0.1;

/**
 * GetVariableBonusTaxx: tax apportionment for a bonus of `bonus` in `month` (0..11) given the 12 anticipated monthly
 * bonuses of the party (the given month is replaced by `bonus`). Fraction = bonus / total for the year;
 * periodsPerYear reports the number of months with a bonus.
 */
export function variableBonusTax(c: Case, party: PartyIndex, month: number, bonus: number, usePriorTable: boolean, opts: EngineOptions = {}): BonusTaxResult {
  const { monthly, table } = bonusData(c, party);
  const m = Math.min(ANNUAL - 1, Math.max(0, Math.floor(Number.isFinite(month) ? month : 0)));
  const amounts = monthly.slice();
  amounts[m] = Number.isFinite(bonus) ? bonus : 0;
  const totalBonus = amounts.reduce((a, b) => a + b, 0);
  const periods = amounts.filter((b) => b !== 0).length;
  const provider = providerOf(opts);
  let annualParts: BonusParts = { ...ZERO_PARTS };
  for (const b of amounts) annualParts = addParts(annualParts, partsOf(table, b, usePriorTable));
  const monthParts = partsOf(table, amounts[m], usePriorTable);
  const fraction = amounts[m] / Math.max(totalBonus, VARIABLE_TOTAL_FLOOR);
  return bonusLines(c, party, amounts[m], periods, totalBonus, annualParts, fraction, monthParts, provider);
}

// ------------------------------------------------------------------------------------- bonus tables

/** Step schedule bands of GetAdditionalWages: [last row of the band, increment]. */
const MONTHLY_BANDS: readonly (readonly [number, number])[] = [[50, 100], [70, 250], [90, 500], [110, 1000], [122, 5000], [142, 10000], [160, 25000]];
const ANNUAL_BANDS: readonly (readonly [number, number])[] = [[200, 25], [250, 100], [290, 250], [350, 500], [400, 1000], [420, 10000], [438, 25000]];
/** Last amount of both schedules (row 160 monthly / row 438 annual). */
export const BONUS_TABLE_MAX_AMOUNT = 750000;

/**
 * §5.3 bonus amounts of the table rows (row 0 = 0): monthly +100 to row 50, +250 to 70, +500 to 90, +1000 to 110,
 * +5000 to 122, +10000 to 142, +25000 to 160 (161 rows); annual +25 to row 200, +100 to 250, +250 to 290, +500 to 350,
 * +1000 to 400, +10000 to 420, +25000 to 438 (439 rows). Rows with an amount above `cap` are dropped.
 */
export function bonusStepSchedule(annual: boolean, cap = Infinity): number[] {
  const out = [0];
  let amount = 0, row = 0;
  for (const [last, step] of annual ? ANNUAL_BANDS : MONTHLY_BANDS) {
    while (row < last) {
      row += 1;
      amount += step;
      if (amount > cap) return out;
      out.push(amount);
    }
  }
  return out;
}

/**
 * Row cap (see bonusTable): monthly amounts stop above 20 × the party's monthly wages + 50,000; annual amounts stop
 * above 12 × that (never beyond the schedule's 750,000).
 */
export function bonusTableCap(c: Case, party: PartyIndex, annual: boolean): number {
  const wages = Math.max(0, c.parties[party].wages || 0);
  const monthlyCap = 20 * wages + 50000;
  return annual ? Math.min(BONUS_TABLE_MAX_AMOUNT, ANNUAL * monthlyCap) : monthlyCap;
}

interface GuidelineRun { g: GuidelineOutcome; netSpendable: [number, number] }

/** Guideline (no proposed column) of a case, with each party's net spendable as the Cash Flow view reports it. */
function runGuideline(c: Case, provider: TaxProvider, now: Date): GuidelineRun {
  const nc = normalizeCase(c);
  const ctx = new TaxContext(nc, provider);
  const g = computeGuideline(ctx, { releases: 0, releaseFrom: 1, now });
  const base = g.noSupportTaxes;
  const ns = [0, 1].map((i) => {
    const p = i as PartyIndex;
    return g.nets[p].formulaNet + (base[p].incomeTax - g.taxes[p].incomeTax) + receivedBy(g.total, p);
  }) as [number, number];
  return { g, netSpendable: ns };
}

/**
 * GetAdditionalWages / GetAnnualBonus / GetBonuses: guideline support at each bonus amount of the step schedule paid
 * to `party` (monthly bonus, or one annual bonus when `annual`), with the prior-relationship parts deducted when
 * `usePriorTable`. Row 0 is the zero-bonus baseline.
 *
 * Row units: `cs`, `ss`, `addOns` and `netSpendable` are the resulting MONTHLY figures (p0-signed support, as
 * computeCase reports). `additionalCs`/`additionalSs`/`taxOnBonus`/`netIncrease` are in the units of `amount`
 * (monthly for the monthly table, annual = 12 × the monthly change for the annual table, as the original's ×12), so
 * netIncrease = amount − taxOnBonus − additionalCs − additionalSs always holds. The "additional" columns are a cost
 * to `party`: an increase in what it pays or a decrease in what it receives. taxOnBonus is the change in the
 * party's prorated no-support taxes (income + employment), which is what the guideline net uses.
 *
 * Row count: the schedule stops once the amount exceeds bonusTableCap (20 × monthly wages + 50,000 per month, or
 * 12 × that per year, at most 750,000), so tables for low earners are shorter than the original's 161 / 439 rows.
 */
export function bonusTable(c: Case, party: PartyIndex, annual: boolean, usePriorTable: boolean, opts: EngineOptions = {}): BonusTable {
  const provider = providerOf(opts);
  const now = opts.now ?? new Date();
  const { table } = bonusData(c, party);
  const paymentsPerYear = annual ? 1 : ANNUAL;
  const toAmountUnits = annual ? ANNUAL : 1; // monthly change → the row's units
  const baseline = runGuideline(c, provider, now);
  const g0 = baseline.g;
  const baseTax = g0.noSupportTaxes[party].taxes.total;
  const rows: BonusTableRow[] = [];
  for (const amount of bonusStepSchedule(annual, bonusTableCap(c, party, annual))) {
    let run = baseline;
    if (amount > 0) {
      const parts = partsOf(table, amount, usePriorTable);
      run = runGuideline(caseWithBonus(c, party, amount * paymentsPerYear, addParts({ ...ZERO_PARTS }, parts, paymentsPerYear)), provider, now);
    }
    const g = run.g;
    const additionalCs = (receivedBy(g0.presumedCs, party) - receivedBy(g.presumedCs, party)) * toAmountUnits;
    const additionalSs = (receivedBy(g0.ss, party) - receivedBy(g.ss, party)) * toAmountUnits;
    const taxOnBonus = (g.noSupportTaxes[party].taxes.total - baseTax) * toAmountUnits;
    const pct = (v: number) => (amount > 0 ? (v / amount) * 100 : 0);
    rows.push({
      amount,
      cs: g.presumedCs, ss: g.ss, addOns: g.addOns,
      csPayor: payorOf(g.presumedCs), ssPayor: payorOf(g.ss),
      additionalCs, additionalSs,
      additionalCsPercent: pct(additionalCs), additionalSsPercent: pct(additionalSs),
      taxOnBonus,
      netIncrease: amount - taxOnBonus - additionalCs - additionalSs,
      netSpendable: run.netSpendable,
    });
  }
  return { party, annual, usePriorTable, rows };
}
